import { useAuth } from "@/contexts/AuthContext";
import { Menu, Sun, Moon, LogOut } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Logo from "../assets/(1024 x 500 px) (1024 x 1024 px) (1).PNG";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function Header({ setShowSettings, setCurrentTab }) {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const dropdownRef = useRef(null);
  const { logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSettingsClick = () => {
    setDropdownOpen(false);
    if (setShowSettings && setCurrentTab) {
      // First switch to the profile tab, then show settings
      setCurrentTab("profile");
      setTimeout(() => {
        setShowSettings(true);
      }, 0); // Small delay to ensure tab switch is processed
    }
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    setShowLogoutDialog(true);
  };

  const confirmLogout = () => {
    setShowLogoutDialog(false);
    logout();
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Main App Header */}
      <div className="flex items-center justify-between p-4 bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
        {/* Center - ScrollEd branding */}
        <div className="h-7 overflow-hidden">
          <img src={Logo} alt="ScrollEd Logo" className="h-full transition-all duration-500" />
        </div>

        {/* Right side - Theme & Profile */}
        <div className="flex items-center gap-2 relative" ref={dropdownRef}>


          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="p-2 rounded-full bg-slate-100/50 dark:bg-white/10 text-slate-900 dark:text-slate-100 hover:scale-110 active:scale-95 transition-all duration-500 flex items-center justify-center border border-transparent hover:border-slate-200 dark:hover:border-white/20 shadow-none focus:outline-none"
          >
            <Menu className="w-5 h-5 opacity-60" />
          </button>

          {/* Dropdown Menu - Liquid Glass */}
          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-3 w-52 bg-white/80 backdrop-blur-3xl border border-white/20 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.15)] overflow-hidden z-50 animate-in fade-in slide-in-from-top-3 zoom-in-95 duration-300">
              <div className="p-1.5">
                <button
                  onClick={handleSettingsClick}
                  className="flex items-center w-full text-left px-4 py-3.5 text-sm font-bold text-slate-800 hover:bg-slate-500/10 rounded-2xl transition-all"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center mr-3">
                    <Sun className="w-4 h-4 text-blue-600" />
                  </div>
                  {t('settings.title')}
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full text-left px-4 py-3.5 text-sm font-bold text-red-600 hover:bg-red-500/10 rounded-2xl transition-all mt-1"
                >
                  <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center mr-3">
                    <LogOut className="w-4 h-4 text-red-600" />
                  </div>
                  {t('auth.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="max-w-[340px] rounded-3xl border-none shadow-2xl bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-black">{t('auth.logout')}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Are you sure you want to sign out of your account?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-3 sm:gap-3 mt-4">
            <AlertDialogCancel className="flex-1 mt-0 rounded-xl h-11 border-slate-200">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLogout}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl h-11"
            >
              {t('settings.sign_out_label') || 'Sign Out'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Header;
